import React from "react";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";

import {
  ConfirmPopUp,
  deactivationGuardOf,
} from "../../../../components/PopUp";
import type { ConfirmDetail } from "../../../../components/PopUp/confirmShared/types";
import { formatCurrency } from "../../../../utils";
import { balanceMovingRowsOf } from "../utils";
import { StatusChangeModalProps } from "./types";

/**
 * Confirms activating or deactivating a saving. Mirrors the Investments and
 * Accounts status modals: a warning-toned confirm with a structured preview of
 * the record and scoped confirm/loading labels.
 */
export const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  isOpen,
  saving,
  onClose,
  onConfirm,
  linkedBrokers = [],
  isCheckingLinkedBrokers = false,
}) => {
  const { name, institution, isActive, currentValue } = saving;

  const transactionsCount = balanceMovingRowsOf(saving).length;
  const transactionsLabel = `${transactionsCount.toLocaleString()} transaction${
    transactionsCount === 1 ? "" : "s"
  }`;
  const subtitle = [institution, transactionsLabel].filter(Boolean).join(" · ");

  const detail: ConfirmDetail = {
    icon: <SavingsRoundedIcon />,
    title: name,
    subtitle,
    amount: formatCurrency(currentValue),
    amountVariant: currentValue < 0 ? "expense" : "neutral",
  };

  const deactivateText =
    "It will be hidden from your active savings and excluded from totals by default. Transfer its balance before deactivating; transactions stay intact and you can reactivate any time.";
  const reactivateText =
    "It will return to your active savings list and be included in totals again. You can deactivate it later at any time.";

  const deactivation = deactivationGuardOf({
    text: deactivateText,
    linkedBrokers,
    isChecking: isCheckingLinkedBrokers,
  });

  const title = isActive ? `Deactivate ${name}?` : `Reactivate ${name}?`;
  const text = isActive ? deactivation.content : reactivateText;
  const isConfirmDisabled = isActive && deactivation.isConfirmDisabled;
  const confirmLabel = isActive ? "Deactivate saving" : "Reactivate saving";
  const loadingLabel = isActive ? "Deactivating…" : "Reactivating…";
  const icon = isActive ? (
    <PauseCircleOutlineRoundedIcon />
  ) : (
    <PlayCircleOutlineRoundedIcon />
  );

  return (
    <ConfirmPopUp
      isOpen={isOpen}
      setIsOpen={onClose}
      tone="warning"
      icon={icon}
      title={title}
      text={text}
      details={detail}
      confirmLabel={confirmLabel}
      loadingLabel={loadingLabel}
      onConfirm={onConfirm}
      isConfirmDisabled={isConfirmDisabled}
    />
  );
};
